// frontend/src/utils/auditClient.js
// クライアント側から audit_logs にレコードを insert するヘルパー。
//
// 使い方:
//   await writeClientAudit({
//     organizationId: '<org-id>',
//     actionType: 'update',
//     targetTable: 'staff_assignments',
//     targetId: '<row-id>',
//     targetSummary: 'staff_assignments 行を編集',
//     changes: { before, after },
//     success: true,
//   });
//
// 制約:
//   - IP/UA はブラウザから取得できないため記録されない (NULL)。
//   - Edge Function 経由の操作は edge 側で記録されるので、client から重複して書かないこと。
//   - 失敗しても呼び出し元の処理を止めないよう例外は握りつぶす。
//
// RLS:
//   audit_logs_insert は WITH CHECK (actor_user_id IS NULL OR actor_user_id = auth.uid())
//   → actor_user_id に自分の auth.uid() を入れる必要あり。

import { supabase } from "../supabase";

// ローカルストレージから現在のユーザー情報 (role 等) を取得
function getActorFromLocalStorage() {
  try {
    const internalRaw = localStorage.getItem("internal_selected_org");
    if (internalRaw) {
      return { role: "admin", isInternal: true };
    }
    const supaUser = localStorage.getItem("supabase_user");
    if (supaUser) {
      const u = JSON.parse(supaUser);
      return { role: u?.role || "staff", isInternal: false, email: u?.email, employeeId: u?.employeeId };
    }
  } catch (_) { /* ignore */ }
  return { role: null, isInternal: false };
}

function classifyActor(role, isInternal) {
  if (isInternal) return "internal_staff";
  if (role === "admin") return "organization_admin";
  if (role) return "organization_staff";
  return "anonymous";
}

/**
 * audit_logs に 1 行書き込む。
 * @param {Object} payload
 * @param {string|null} payload.organizationId
 * @param {string} payload.actionType - 'create' | 'update' | 'delete' | 'read' | 'access_denied' | 'other' | ...
 * @param {string|null} payload.targetTable
 * @param {string|null} payload.targetId
 * @param {string|null} payload.targetSummary
 * @param {Object|null} payload.changes
 * @param {boolean} payload.success  (default: true)
 * @param {string|null} payload.errorMessage
 */
export async function writeClientAudit(payload) {
  try {
    // 現在認証されている Supabase ユーザー
    const { data: userData } = await supabase.auth.getUser();
    const authUser = userData?.user || null;
    if (!authUser) return; // 未認証ならスキップ (RLS でも通らない)

    const { role, isInternal, email } = getActorFromLocalStorage();
    const actorType = classifyActor(role, isInternal);

    const row = {
      organization_id: payload.organizationId ?? null,
      actor_user_id: authUser.id,
      actor_email: authUser.email || email || null,
      actor_type: actorType,
      actor_role: role,
      action_type: payload.actionType,
      target_table: payload.targetTable ?? null,
      target_id: payload.targetId ?? null,
      target_summary: payload.targetSummary ?? null,
      changes: payload.changes ?? null,
      // ip_address / user_agent はクライアントからは取れないので NULL
      success: payload.success ?? true,
      error_message: payload.errorMessage ?? null,
    };

    const { error } = await supabase.from("audit_logs").insert(row);
    if (error) {
      console.warn("audit_logs insert failed (non-fatal):", error.message);
    }
  } catch (e) {
    console.warn("writeClientAudit threw (ignored):", e?.message || e);
  }
}
